import {
  Button,
  Input,
  Popconfirm,
  Select,
  Space,
  Switch,
  TableProps,
  Tooltip,
} from "antd";
import { capitalize } from "lodash";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../../app/store";
import AddIcon from "../../../assets/icons/AddIcon";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import EditIcon from "../../../assets/icons/EditIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import CustomTable from "../../../components/ui/CustomTable";
import Loader from "../../../components/ui/Loader";
import {
  useDeletePortalDepartmentMutation,
  useGetProtalAllDepartmentQuery,
  useUpdatePortalStatusDepartmentMutation,
} from "../../../features/portal/settings/settingsApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { formatDate } from "../../../lib/time";
import { toast } from "../../../lib/toast";
import {
  GetProtalAllDepartmentDataType,
  GetProtalAllDepartmentParamsType,
} from "../../../types/portal/settings";
import { countParPageSerialNumber } from "../../../utils/number";
import { addKeyInArray, selectSearchOption } from "../../../utils/utils";
import ManageDepartment from "./_components/ManageDepartment";

enum DepartmentStatus {
  "Inactive",
  "Active",
}

export default function Department() {
  const [isViewOpen, setIsViewOpen] = useState(false);

  const {
    page,
    limit,
    handlePagination,
    handleFilter,
    handleLimit,
    filter,
    keyword,
    handleSearch,
  } = useTable<GetProtalAllDepartmentParamsType>();
  const { open, handleOpen, handleClose } = useModal<string, any>();
  const {
    open: productActivityOpen,
    handleOpen: handleProductActivityOpen,
    handleClose: handleProductActivityClose,
  } = useModal<string, any>();
  const { user } = useAppSelector((state) => state.auth);

  const { data, isError, error, isLoading } = useGetProtalAllDepartmentQuery({
    params: {
      ...filter,
      skip: undefined,
      page,
      search: keyword,
      company_id: user?.data?.company_id,
    },
  });
  const [
    updatePortalStatusDepartment,
    {
      isSuccess: isStatusSuccess,
      isLoading: isStatusLoading,
      isError: isStatusError,
      error: statusError,
    },
  ] = useUpdatePortalStatusDepartmentMutation();
  const [
    deletePortalDepartment,
    {
      isSuccess: isDeleteSuccess,
      isLoading: isDeleteLoading,
      isError: isDeleteError,
      error: deleteError,
    },
  ] = useDeletePortalDepartmentMutation();
  useError(isError, error, "Get all department data failed");
  useError(isStatusError, statusError, "Status update failed");
  useError(isDeleteError, deleteError, "Department delete failed");

  const columns: TableProps<
    GetProtalAllDepartmentDataType["data"]["departments"][0]
  >["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Department Name",
      dataIndex: "name",
      render: (val) => (val ? val : "N/A"),
    },
    // {
    //   title: "Parent Company",
    //   dataIndex: "parent_company",
    //   render: (val) => (val ? val : "N/A"),
    // },
    {
      title: "Head of Department",
      dataIndex: "headOfDepartment",
      render: (val) => (val?.username ? capitalize(val?.username) : "N/A"),
    },
    {
      title: "Abbreviation",
      dataIndex: "abbreviation",
      render: (val) => val || "-",
    },
    {
      title: "Total Employees",
      dataIndex: "employee_count",
      align: "center",
      render: (val) => (val ? val : "0"),
    },
    {
      title: "Created at",
      dataIndex: "created_at",
      align: "center",
      render: (val) => (val ? formatDate(val) : "N/A"),
    },

    {
      title: "Status",
      dataIndex: "is_published",
      render: (val, record) => (
        <Space size={4}>
          <Switch
            className="custom-switch"
            checked={val === 1}
            onChange={() =>
              updatePortalStatusDepartment({
                data: {
                  is_published: val === 0 ? 1 : 0,
                },
                id: record?.id,
              })
            }
            disabled={isStatusLoading}
          />
          <span className="text-header text-sm font-medium">
            {DepartmentStatus[val]}
          </span>
        </Space>
      ),
    },
    {
      title: "Actions",
      align: "center",
      render: (record: any) => (
        <div className="flex justify-center items-center gap-2">
          <Tooltip title="Edit Department">
            <Button
              className="action-button"
              size="small"
              icon={<EditIcon />}
              onClick={() => {
                handleOpen({
                  type: "UPDATE",
                  data: record,
                });
              }}
            />
          </Tooltip>{" "}
          <Tooltip title="View Department">
            <Button
              className="action-button"
              size="small"
              icon={<ViewIcon />}
              onClick={() => {
                handleOpen({
                  type: "VIEW",
                  data: record,
                });
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete department"
            description="Are you sure you want to delete this department?"
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true, loading: isDeleteLoading }}
            onConfirm={() => deletePortalDepartment(record?.id)}
          >
            <Tooltip title="Delete Department">
              <Button
                danger
                className="action-button"
                size="small"
                icon={<DeleteIcon />}
              />
            </Tooltip>
          </Popconfirm>
          {/* <Tooltip title="Edit Status">
            <Button
              className="action-button"
              size="small"
              icon={<RefreshIcon />}
              onClick={() =>
                handleProductActivityOpen({
                  type: "PRODUCT_ACTIVITY",
                  data: record,
                })
              }
            />
          </Tooltip> */}
        </div>
      ),
    },
  ];

  const handleCloseView = () => {
    setIsViewOpen(false);
    handleClose();
  };

  useEffect(() => {
    if (isStatusSuccess) {
      toast("success", "Status updated successfully");
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStatusSuccess]);

  useEffect(() => {
    if (isDeleteSuccess) {
      toast("success", "Department deleted successfully");
    }
  }, [isDeleteSuccess]);

  return (
    <>
      {(open?.type === "ADD" ||
        open?.type === "UPDATE" ||
        open?.type === "VIEW") && (
        <ManageDepartment
          open={open?.open}
          data={open?.data}
          type={open?.type}
          handleClose={handleCloseView}
        />
      )}

      {/* {productActivityOpen?.type === "PRODUCT_ACTIVITY" && (
        <DepartmentStatusUpdateModal
          title=""
          subTitle=""
          buttonName="Send"
          open={productActivityOpen?.open}
          data={productActivityOpen?.data}
          handleClose={handleProductActivityClose}
          action
          buttonBgDeep
          loading={isLoading}
        />
      )} */}

      <SidebarLayout title="Department List">
        {isLoading ? (
          <Loader />
        ) : (
          <CustomTable
            loading={isLoading}
            limit={limit}
            page={page}
            columns={columns}
            total={data?.data?.totalLength || 0}
            dataSource={addKeyInArray(data?.data?.departments)}
            handleLimit={handleLimit}
            handlePagination={handlePagination}
            filterSection={
              <>
                <Input
                  suffix={<SearchIcon />}
                  placeholder="Search by department name"
                  style={{ width: 300 }}
                  allowClear
                  onChange={handleSearch}
                />
                <Select
                  allowClear
                  showSearch
                  placeholder="Status"
                  filterOption={selectSearchOption}
                  options={[
                    { label: "Active", value: 1 },
                    { label: "Inactive", value: 0 },
                  ]}
                  onChange={(value) => handleFilter("is_published", value)}
                  className="min-w-[90px]"
                  suffixIcon={<SelectDownIcon />}
                  rootClassName="icon-gray"
                />

                <Button
                  type="primary"
                  className="middle-button"
                  icon={<AddIcon />}
                  onClick={() =>
                    handleOpen({
                      type: "ADD",
                    })
                  }
                >
                  Add New Department
                </Button>
              </>
            }
          />
        )}
      </SidebarLayout>
    </>
  );
}
